import { memoize, uuid, getFileExtension } from "lib";
import { VIDEO_MEDIA_TYPES } from "lib/constants";
import { extractGifFrameCanvases } from "./gifFrameExtractor";

// Cache for GIF frame data (all frames extracted once)
const gifFrameCache = new Map();

const IMAGE_SOURCE_RETRY_TIME = 500;
const MAX_RETRY = 20;
// Cap the decoded-image cache so a long editing session that cycles through
// many per-textbox blob URLs can't retain Image objects for the whole session.
const MAX_TEXT_MASK_IMAGE_CACHE = 32;
const textMaskImageCache = new Map();

// Largest baked canvas dimension allowed during a live drag preview. Capping the
// resolution keeps the per-frame canvas encode cheap so rotation can track the
// pointer; the committed render re-bakes at full resolution.
const PREVIEW_MAX_DIMENSION = 700;

const getLoadedTextMaskImage = source => {
  const cached = textMaskImageCache.get(source);
  if (cached) {
    return cached;
  }

  const loadPromise = new Promise((resolve, reject) => {
    const image = new Image();
    image.onload = () => resolve(image);
    image.onerror = () => {
      // Don't keep a rejected load cached — drop it so a later attempt can
      // retry instead of permanently poisoning this source.
      textMaskImageCache.delete(source);
      reject(new Error(`Failed to load text mask image: ${source}`));
    };
    image.src = source;
  });

  // Evict the oldest entry once the cache is full (Map preserves insertion
  // order, so the first key is the least-recently added).
  if (textMaskImageCache.size >= MAX_TEXT_MASK_IMAGE_CACHE) {
    const oldest = textMaskImageCache.keys().next().value;
    textMaskImageCache.delete(oldest);
  }
  textMaskImageCache.set(source, loadPromise);
  return loadPromise;
};

const clampColor = value => Math.max(0, Math.min(255, Math.round(value)));

const hueRotatePixel = (red, green, blue, degrees) => {
  if (!degrees) return [red, green, blue];

  const radians = (degrees * Math.PI) / 180;
  const cos = Math.cos(radians);
  const sin = Math.sin(radians);

  return [
    (0.213 + cos * 0.787 - sin * 0.213) * red +
      (0.715 - cos * 0.715 - sin * 0.715) * green +
      (0.072 - cos * 0.072 + sin * 0.928) * blue,
    (0.213 - cos * 0.213 + sin * 0.143) * red +
      (0.715 + cos * 0.285 + sin * 0.14) * green +
      (0.072 - cos * 0.072 - sin * 0.283) * blue,
    (0.213 - cos * 0.213 - sin * 0.787) * red +
      (0.715 - cos * 0.715 + sin * 0.715) * green +
      (0.072 + cos * 0.928 + sin * 0.072) * blue
  ];
};

export const applyFilterSettingsToImageData = (imageData, settings = {}) => {
  const data = imageData.data;

  const brightness = settings.brightness ?? 1;
  const contrast = (settings.contrast ?? 100) / 100;
  const saturation = (settings.saturation ?? 100) / 100;
  const grayscale = (settings.grayscale ?? 0) / 100;
  const invert = (settings.invert ?? 0) / 100;
  const sepia = (settings.sepia ?? 0) / 100;
  const hue = settings.hue ?? 0;

  for (let index = 0; index < data.length; index += 4) {
    let red = data[index];
    let green = data[index + 1];
    let blue = data[index + 2];

    red *= brightness;
    green *= brightness;
    blue *= brightness;

    red = (red - 128) * contrast + 128;
    green = (green - 128) * contrast + 128;
    blue = (blue - 128) * contrast + 128;

    const grayscaleValue = red * 0.2126 + green * 0.7152 + blue * 0.0722;

    red = grayscaleValue + (red - grayscaleValue) * saturation;
    green = grayscaleValue + (green - grayscaleValue) * saturation;
    blue = grayscaleValue + (blue - grayscaleValue) * saturation;

    red = red * (1 - grayscale) + grayscaleValue * grayscale;
    green = green * (1 - grayscale) + grayscaleValue * grayscale;
    blue = blue * (1 - grayscale) + grayscaleValue * grayscale;

    red = red * (1 - invert) + (255 - red) * invert;
    green = green * (1 - invert) + (255 - green) * invert;
    blue = blue * (1 - invert) + (255 - blue) * invert;

    if (sepia) {
      const sepiaRed = red * 0.393 + green * 0.769 + blue * 0.189;
      const sepiaGreen = red * 0.349 + green * 0.686 + blue * 0.168;
      const sepiaBlue = red * 0.272 + green * 0.534 + blue * 0.131;

      red = red * (1 - sepia) + sepiaRed * sepia;
      green = green * (1 - sepia) + sepiaGreen * sepia;
      blue = blue * (1 - sepia) + sepiaBlue * sepia;
    }

    [red, green, blue] = hueRotatePixel(red, green, blue, hue);

    data[index] = clampColor(red);
    data[index + 1] = clampColor(green);
    data[index + 2] = clampColor(blue);
  }

  return imageData;
};

export const applyImageFilterSettings = (ctx, width, height, settings = {}) => {
  const imageData = ctx.getImageData(0, 0, width, height);
  ctx.putImageData(applyFilterSettingsToImageData(imageData, settings), 0, 0);
};

// Neutral values for the colour adjustments the per-pixel path bakes (blur is
// handled separately via the canvas filter, so it is intentionally excluded).
const COLOUR_ADJUSTMENT_DEFAULTS = {
  brightness: 1,
  contrast: 100,
  saturation: 100,
  grayscale: 0,
  invert: 0,
  sepia: 0,
  hue: 0
};

// Whether the settings contain a real colour change that needs the Safari-safe
// per-pixel bake. Neutral and blur-only masks don't, so they can stay on the
// cheap draw path.
const hasColourAdjustment = settings =>
  !!settings &&
  Object.keys(COLOUR_ADJUSTMENT_DEFAULTS).some(
    key =>
      Number(settings[key] ?? COLOUR_ADJUSTMENT_DEFAULTS[key]) !==
      COLOUR_ADJUSTMENT_DEFAULTS[key]
  );

const createFilteredImageCanvas = ({ image, width, height, filters, filterSettings }) => {
  const imageCanvas = document.createElement("canvas");
  imageCanvas.width = width;
  imageCanvas.height = height;
  const imageCtx = imageCanvas.getContext("2d");

  const blurValue = filterSettings && filterSettings.blur ? filterSettings.blur : 0;

  if (hasColourAdjustment(filterSettings)) {
    // Real colour adjustment: bake colours per-pixel (Safari-safe, #708). The
    // per-pixel path can't express blur, so apply blur via the canvas filter
    // first (Safari handles blur fine), then run the colour adjustments.
    if (blurValue) {
      imageCtx.filter = `blur(${blurValue}px)`;
    }
    imageCtx.drawImage(image, 0, 0, width, height);
    imageCtx.filter = "none";
    applyImageFilterSettings(imageCtx, width, height, filterSettings);
  } else {
    // Neutral or blur-only mask: skip the per-pixel scan entirely (it would be
    // a no-op) and use the cheap canvas-filter draw so rotation previews stay
    // fast on large masks.
    if (blurValue) {
      imageCtx.filter = `blur(${blurValue}px)`;
    } else if (!filterSettings && filters && filters.length > 0) {
      imageCtx.filter = filters;
    }
    imageCtx.drawImage(image, 0, 0, width, height);
  }

  return imageCanvas;
};

const getSourceImage = async (gifSource, resolve, reject, attempt = 1) => {
  if (attempt < MAX_RETRY) {
    try {
      const sourceImage = await fetch(`${gifSource}?${uuid()}`);
      if (!sourceImage.ok) {
        setTimeout(() => {
          getSourceImage(gifSource, resolve, reject, attempt + 1);
        }, IMAGE_SOURCE_RETRY_TIME);
      } else {
        return resolve(sourceImage);
      }
    } catch (error) {
      // fetch() throws on network/CORS errors — reject immediately instead of
      // leaving the outer promise hanging (the async function's implicit promise
      // rejection would go unhandled without this)
      return reject(error);
    }
  } else {
    return reject("");
  }
};

export const getTextMaskSource = ({
  color,
  source,
  filters,
  filterSettings,
  canvas,
  opacity,
  width: displayWidth,
  height: displayHeight,
  rotation = 0,
  useDataUrl = false
} = {}) =>
  new Promise(async (resolve, reject) => {
    let tempImg;
    try {
      tempImg = await getLoadedTextMaskImage(source);
    } catch (error) {
      // Settle the outer promise so callers (e.g. Textbox.setImageMaskSource)
      // can clear their in-progress state instead of hanging forever when the
      // mask image fails to load.
      reject(error);
      return;
    }
    const ctx = canvas.getContext("2d");
    // remove any old content on the canvas
    ctx.clearRect(0, 0, canvas.width, canvas.height);
    const shouldBakeRotation = Number(rotation || 0) !== 0;
    const baseImageWidth = shouldBakeRotation
      ? Number(displayWidth) || tempImg.naturalWidth || tempImg.width
      : tempImg.naturalWidth || tempImg.width;
    const baseImageHeight = shouldBakeRotation
      ? Number(displayHeight) || tempImg.naturalHeight || tempImg.height
      : tempImg.naturalHeight || tempImg.height;
    const rotationRadians = (Number(rotation || 0) * Math.PI) / 180;
    const baseCanvasWidth = shouldBakeRotation
      ? Math.abs(baseImageWidth * Math.cos(rotationRadians)) +
        Math.abs(baseImageHeight * Math.sin(rotationRadians))
      : baseImageWidth;
    const baseCanvasHeight = shouldBakeRotation
      ? Math.abs(baseImageWidth * Math.sin(rotationRadians)) +
        Math.abs(baseImageHeight * Math.cos(rotationRadians))
      : baseImageHeight;

    // During a live drag preview, cap the baked resolution so the per-frame
    // encode stays cheap and the rotation can track the pointer. Geometry is
    // unchanged — CSS backgroundSize scales the image to the same box — so the
    // only visible difference is a slightly softer image until commit re-bakes
    // it at full resolution.
    const largestBaseDimension = Math.max(baseCanvasWidth, baseCanvasHeight);
    const previewScale =
      useDataUrl && largestBaseDimension > PREVIEW_MAX_DIMENSION
        ? PREVIEW_MAX_DIMENSION / largestBaseDimension
        : 1;
    const imageWidth = baseImageWidth * previewScale;
    const imageHeight = baseImageHeight * previewScale;
    const canvasWidth = baseCanvasWidth * previewScale;
    const canvasHeight = baseCanvasHeight * previewScale;

    // set the canvas dimensions for the rendered background image
    canvas.width = Math.ceil(canvasWidth);
    canvas.height = Math.ceil(canvasHeight);

    // draw the background colour behind the image
    ctx.beginPath();
    ctx.strokeStyle = color;
    ctx.fillStyle = color;
    ctx.rect(0, 0, canvasWidth, canvasHeight);
    ctx.stroke();
    ctx.fill();

    ctx.save();
    if (shouldBakeRotation) {
      ctx.translate(canvasWidth / 2, canvasHeight / 2);
      ctx.rotate(rotationRadians);
    }
    // apply opacity only to the image
    if (opacity) {
      ctx.globalAlpha = opacity;
    }
    // Apply filters on an offscreen canvas: filterSettings uses Safari-safe
    // per-pixel filtering, otherwise it falls back to ctx.filter (#708).
    const filteredImageCanvas = createFilteredImageCanvas({
      image: tempImg,
      width: imageWidth,
      height: imageHeight,
      filters,
      filterSettings
    });
    // add the (filtered) image as an overlay on the block colour background
    if (shouldBakeRotation) {
      ctx.drawImage(
        filteredImageCanvas,
        -imageWidth / 2,
        -imageHeight / 2,
        imageWidth,
        imageHeight
      );
    } else {
      ctx.drawImage(filteredImageCanvas, 0, 0);
    }
    ctx.restore();

    if (useDataUrl) {
      // Live drag previews need to swap quickly and do not need the final
      // lossless PNG encode. JPEG keeps the same full-size geometry but avoids
      // the much heavier PNG data URL work that made slider/handle drags lag.
      resolve(canvas.toDataURL("image/jpeg", 0.9));
      return;
    }

    canvas.toBlob(blob => {
      if (!blob) {
        resolve(canvas.toDataURL());
        return;
      }
      resolve(window.URL.createObjectURL(blob));
    });
  });

export const getSourceAsLocalUrl = async gifSource => {
  if (gifSource.startsWith("blob:")) {
    // when the url is already a local one we don't need to process it any more
    return gifSource;
  } else {
    let image;
    try {
      image = await new Promise((resolve, reject) =>
        getSourceImage(gifSource, resolve, reject)
      );
    } catch (error) {
      console.error(
        new Error(
          `source '${gifSource}' failed to be loaded after ${MAX_RETRY} attempts`
        )
      );
      return null;
    }

    const imageBlob = await image.blob();
    return window.URL.createObjectURL(imageBlob);
  }
};

export const getGifImageSource = async ({ isPlaying, gifSource }) => {
  if (!isPlaying) {
    const sourceAsLocalUrl = await getSourceAsLocalUrl(gifSource);

    if (!sourceAsLocalUrl) {
      return "";
    }

    const frameData = await extractGifFrameCanvases(sourceAsLocalUrl);
    return frameData[0].getImage().toDataURL();
  }
  return gifSource;
};

export const memoizedGetGifImageSource = memoize(
  getGifImageSource,
  ({ isPlaying, gifSource, isLooped }) =>
    `${Boolean(isPlaying)}-${Boolean(isLooped)}-${gifSource}`
);

export const isMediaVideoFormat = mediaSource => {
  if (!mediaSource || !(typeof mediaSource === "string")) {
    return false;
  }
  const sourceSuffix = mediaSource.split(".").pop();
  if (!sourceSuffix) {
    return false;
  }

  return VIDEO_MEDIA_TYPES.includes(sourceSuffix.toLowerCase());
};

export const getMediaType = media => {
  const fileExtension = getFileExtension(media.name);

  if (fileExtension === "gif") {
    return "ANIMATION";
  } else if (fileExtension === "mp4") {
    return "VIDEO";
  } else {
    return "IMAGE";
  }
};

/**
 * Extracts all frames from a GIF and caches them.
 * Returns an array of frame data with timing info.
 *
 * @param {string} gifSource - The URL of the GIF
 * @returns {Promise<{frames: Array, totalDuration: number}>}
 */
export const extractGifFrames = async gifSource => {
  if (gifFrameCache.has(gifSource)) {
    return gifFrameCache.get(gifSource);
  }

  const sourceAsLocalUrl = await getSourceAsLocalUrl(gifSource);
  if (!sourceAsLocalUrl) {
    return { frames: [], totalDuration: 0 };
  }

  const frameData = await extractGifFrameCanvases(sourceAsLocalUrl);

  // Extract frame info with timing (GIF delay is in centiseconds)
  // Clone each canvas immediately and convert to data URL to preserve frame state
  const frames = frameData.map(frame => {
    const sourceCanvas = frame.getImage();
    // Clone the canvas to preserve this frame's state
    const clonedCanvas = document.createElement("canvas");
    clonedCanvas.width = sourceCanvas.width;
    clonedCanvas.height = sourceCanvas.height;
    const ctx = clonedCanvas.getContext("2d");
    ctx.drawImage(sourceCanvas, 0, 0);
    // Convert to data URL immediately to freeze the frame
    const dataUrl = clonedCanvas.toDataURL();

    return {
      dataUrl,
      delay: (frame.frameInfo.delay || 10) * 10 // Convert to milliseconds
    };
  });

  // Calculate total duration
  let totalDuration = 0;
  frames.forEach(f => {
    totalDuration += f.delay;
  });

  const result = { frames, totalDuration };
  gifFrameCache.set(gifSource, result);
  return result;
};

/**
 * Gets a specific frame from a GIF based on timeline position.
 * Used for scrubbing GIF text masks.
 *
 * @param {string} gifSource - The URL of the GIF
 * @param {number} timeMs - The time position in milliseconds
 * @param {boolean} loop - Whether the GIF should loop
 * @returns {Promise<string>} - Data URL of the frame
 */
export const getGifFrameAtTime = async (gifSource, timeMs, loop = true) => {
  const { frames, totalDuration } = await extractGifFrames(gifSource);

  if (frames.length === 0) {
    return "";
  }

  // Handle looping
  let targetTime = timeMs;
  if (loop && totalDuration > 0) {
    targetTime = timeMs % totalDuration;
  } else if (timeMs >= totalDuration) {
    // Return last frame if not looping and past duration
    return frames[frames.length - 1].dataUrl;
  }

  // Find the frame at the target time
  let accumulatedTime = 0;
  for (let i = 0; i < frames.length; i++) {
    accumulatedTime += frames[i].delay;
    if (targetTime < accumulatedTime) {
      return frames[i].dataUrl;
    }
  }

  // Fallback to last frame
  return frames[frames.length - 1].dataUrl;
};

/**
 * Preloads GIF frames for faster scrubbing.
 * Call this when a GIF element is first rendered.
 *
 * @param {string} gifSource - The URL of the GIF to preload
 */
export const preloadGifFrames = gifSource => {
  extractGifFrames(gifSource).catch(err => {
    console.warn("Failed to preload GIF frames:", err);
  });
};
